// Shared constants + parsing helpers mirroring the iOS models.
// Status strings MUST match the live Firestore data exactly.

export const OrderStatus = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PREPARING: 'Preparing',
  READY: 'Ready',
  PICKED_UP: 'Picked Up',
  ON_MY_WAY: 'On My Way',
  IN_DELIVERY: 'In Delivery',
  ARRIVED: 'Arrived',
  DELIVERED: 'Delivered',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

// Statuses written to drivers/{driverId}/acceptedOrders/{orderId}
export const DriverOrderStatus = {
  ACCEPTED: 'Accepted',
  NAVIGATE_TO_RESTAURANT: 'Navigate to Restaurant',
  ARRIVED_AT_RESTAURANT: 'Arrived at Restaurant',
  PICKED_UP_ORDER: 'Picked Up Order',
  ON_MY_WAY: 'On My Way',
  IN_DELIVERY: 'In Delivery',
  ARRIVED_AT_CUSTOMER: 'Arrived at Customer',
  DELIVERED: 'Delivered',
  COMPLETED: 'Completed',
};

// The 6-step driver workflow shown on OrderCompleteCardView
export const DRIVER_WORKFLOW_STEPS = [
  DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
  DriverOrderStatus.ARRIVED_AT_RESTAURANT,
  DriverOrderStatus.PICKED_UP_ORDER,
  DriverOrderStatus.ON_MY_WAY,
  DriverOrderStatus.ARRIVED_AT_CUSTOMER,
  DriverOrderStatus.DELIVERED,
];

// FirebaseManager.updateDriverOrderStatus mapping: driver status -> main
// order status. iOS collapses pick-up through arrival into "In Delivery",
// and maps both terminal states to "Delivered".
export const DRIVER_TO_ORDER_STATUS = {
  [DriverOrderStatus.ACCEPTED]: OrderStatus.CONFIRMED,
  [DriverOrderStatus.NAVIGATE_TO_RESTAURANT]: OrderStatus.CONFIRMED,
  [DriverOrderStatus.ARRIVED_AT_RESTAURANT]: OrderStatus.CONFIRMED,
  [DriverOrderStatus.PICKED_UP_ORDER]: OrderStatus.IN_DELIVERY,
  [DriverOrderStatus.ON_MY_WAY]: OrderStatus.IN_DELIVERY,
  [DriverOrderStatus.IN_DELIVERY]: OrderStatus.IN_DELIVERY,
  [DriverOrderStatus.ARRIVED_AT_CUSTOMER]: OrderStatus.IN_DELIVERY,
  [DriverOrderStatus.DELIVERED]: OrderStatus.DELIVERED,
  [DriverOrderStatus.COMPLETED]: OrderStatus.DELIVERED,
};

// Statuses that count toward the 2-delivery driver cap
export const ACTIVE_DRIVER_STATUSES = new Set([
  DriverOrderStatus.ACCEPTED,
  DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
  DriverOrderStatus.ARRIVED_AT_RESTAURANT,
  DriverOrderStatus.PICKED_UP_ORDER,
  DriverOrderStatus.ON_MY_WAY,
  DriverOrderStatus.IN_DELIVERY,
  DriverOrderStatus.ARRIVED_AT_CUSTOMER,
]);

export const MAX_ACTIVE_DELIVERIES = 2;
export const DELIVERY_PROXIMITY_FEET = 200;
// iOS ProximityNotificationManager radius — customer "Driver Nearby" alert.
export const DRIVER_NEARBY_METERS = 804.672; // half a mile
export const RESTAURANT_PAYOUT_RATE = 0.90;
export const DRIVER_COMMISSION_RATE = 0.90;
export const PLATFORM_COMMISSION_RATE = 0.10;

// Case-insensitive accepted-order status parsing (mirrors iOS fallback for
// legacy lowercase values).
export function parseDriverStatus(raw) {
  if (!raw || typeof raw !== 'string') return DriverOrderStatus.ACCEPTED;
  const v = raw.trim().toLowerCase();
  const map = {
    'accepted': DriverOrderStatus.ACCEPTED,
    'navigate to restaurant': DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
    'arrived at restaurant': DriverOrderStatus.ARRIVED_AT_RESTAURANT,
    'picked up order': DriverOrderStatus.PICKED_UP_ORDER,
    'picked up': DriverOrderStatus.PICKED_UP_ORDER,
    'on my way': DriverOrderStatus.ON_MY_WAY,
    'in delivery': DriverOrderStatus.IN_DELIVERY,
    'arrived at customer': DriverOrderStatus.ARRIVED_AT_CUSTOMER,
    'arrived': DriverOrderStatus.ARRIVED_AT_CUSTOMER,
    'delivered': DriverOrderStatus.DELIVERED,
    'completed': DriverOrderStatus.COMPLETED,
    'cancelled': 'Cancelled',
  };
  return map[v] || DriverOrderStatus.ACCEPTED;
}

export function parseOrderStatus(raw) {
  if (!raw || typeof raw !== 'string') return OrderStatus.PENDING;
  const v = raw.trim().toLowerCase();
  const found = Object.values(OrderStatus).find(s => s.toLowerCase() === v);
  if (found) return found;
  // Legacy camelCase values written by the validateOrderQRCode function and
  // older clients (e.g. 'pickedUpOrder', 'inDelivery').
  const camel = {
    'pickeduporder': OrderStatus.PICKED_UP,
    'onmyway': OrderStatus.ON_MY_WAY,
    'indelivery': OrderStatus.IN_DELIVERY,
    'arrivedatcustomer': OrderStatus.ARRIVED,
    'arrivedatrestaurant': OrderStatus.CONFIRMED,
    'accepted': OrderStatus.CONFIRMED,
  };
  return camel[v] || raw;
}

// ---------- Formatting ----------
export const formatCurrency = (n) =>
  `$${(Number(n) || 0).toFixed(2)}`;

export const formatDate = (d) => {
  if (!d) return '';
  const date = toDate(d);
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

export const formatTime = (d) => {
  if (!d) return '';
  const date = toDate(d);
  return date ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
};

export const formatDateTime = (d) => {
  const date = toDate(d);
  return date ? `${formatDate(date)} ${formatTime(date)}` : '';
};

export const timeAgo = (d) => {
  const date = toDate(d);
  if (!date) return '';
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Firestore Timestamp | Date | seconds-obj | ISO string -> Date|null
export function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v === 'object' && 'seconds' in v) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

// GeoPoint | {latitude,longitude} | {lat,lng} | {lat,lon} -> {lat, lng} | null
export function toLatLng(v) {
  if (!v) return null;
  const lat = v.latitude ?? v.lat;
  const lng = v.longitude ?? v.lng ?? v.lon;
  if (lat == null || lng == null) return null;
  const latN = Number(lat);
  const lngN = Number(lng);
  // NaN/Infinity must fail closed — a NaN distance defeats the
  // delivery-proximity gate because NaN > limit evaluates false.
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return null;
  return { lat: latN, lng: lngN };
}

// Great-circle distance in feet. Returns null for any non-finite input —
// NaN must never reach the proximity gate (NaN > limit is false).
export function distanceFeet(a, b) {
  if (!a || !b ||
      !Number.isFinite(a.lat) || !Number.isFinite(a.lng) ||
      !Number.isFinite(b.lat) || !Number.isFinite(b.lng)) return null;
  const R = 20902231; // earth radius in feet
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  const d = 2 * R * Math.asin(Math.sqrt(Math.min(1, Math.max(0, s))));
  return Number.isFinite(d) ? d : null;
}

export function distanceMiles(a, b) {
  const ft = distanceFeet(a, b);
  return ft == null ? null : ft / 5280;
}

export const statusColor = (status) => ({
  'Pending': 'orange',
  'Confirmed': 'blue',
  'Preparing': 'purple',
  'Ready': 'green',
  'Picked Up': 'indigo',
  'On My Way': 'cyan',
  'In Delivery': 'cyan',
  'Arrived': 'teal',
  'Delivered': 'green',
  'Completed': 'green',
  'Cancelled': 'red',
}[status] || 'gray');

// Payout calculations (mirror iOS exactly)
export const restaurantPayout = (order) => {
  const base = order.subtotal ?? order.totalAmount ?? 0;
  return Number(order.restaurantPayout ?? base * RESTAURANT_PAYOUT_RATE);
};
// Creation-time driverPayout excludes the tip; the accept-time value includes
// it. max() resolves either, plus orders missing the field entirely.
export const driverPayoutFor = (order) =>
  Math.max(
    Number(order.driverPayout) || 0,
    ((order.deliveryFee || 0) * DRIVER_COMMISSION_RATE) + (order.tipAmount || 0)
  );

// ---------- Island normalization (mirrors iOS Island enum) ----------
// Firestore docs hold several spellings: 'St. Kitts', 'Saint Kitts',
// 'saintKitts', 'saint_kitts', 'Nevis', 'nevis'.
export function normalizeIsland(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const v = raw.toLowerCase().replace(/[^a-z]/g, '');
  if (v === 'nevis') return 'Nevis';
  if (v === 'stkitts' || v === 'saintkitts' || v === 'stkittsandnevis') return 'St. Kitts';
  return null;
}

// Rough island bounding box — same heuristic as LocationPicker.detectIsland.
export function islandFromCoords(coords) {
  const c = toLatLng(coords);
  if (!c) return null;
  if (c.lat >= 17.09 && c.lat <= 17.22 && c.lng >= -62.65 && c.lng <= -62.54) return 'Nevis';
  if (c.lat >= 17.2 && c.lat <= 17.43 && c.lng >= -62.9 && c.lng <= -62.5) return 'St. Kitts';
  return null;
}

// iOS DriverManager island check: restaurantLocation.island ==
// customerLocation.island == driver.island (customer island defaults to
// St. Kitts when absent). We infer from coordinates when the stored island
// string is missing so legacy orders still classify correctly.
export function orderRestaurantIsland(order) {
  const loc = order.restaurantLocation || order.raw?.restaurantLocation || {};
  return normalizeIsland(loc.island) || islandFromCoords(loc.coordinates || loc);
}

export function orderCustomerIsland(order) {
  const cust = order.customerLocation || order.raw?.customerLocation;
  const addr = order.deliveryAddress || order.raw?.deliveryAddress;
  return (
    normalizeIsland(cust?.island) ||
    normalizeIsland(addr?.island) ||
    islandFromCoords(cust?.coordinates || cust) ||
    islandFromCoords(addr?.coordinates || addr) ||
    'St. Kitts' // iOS default when the order carries no island data
  );
}

// iOS: drivers only see orders where BOTH endpoints are on their island.
export function orderMatchesDriverIsland(order, driverIsland) {
  const di = normalizeIsland(driverIsland);
  if (!di) return true; // no island on file — don't hide orders
  return orderRestaurantIsland(order) === di && orderCustomerIsland(order) === di;
}

// ---------- Order normalization ----------
// Converts a Firestore order doc into a plain object matching the iOS Order model.
export function parseOrder(id, d) {
  const items = (d.orderItems || d.items || []).map(it => {
    // Web checkout nests item details inside a `menuItem` object
    const mi = it.menuItem || {};
    const price = it.price ?? mi.price ?? 0;
    return {
      id: it.id || it.menuItemId || mi.id || '',
      menuItemId: it.menuItemId || it.id || mi.id || '',
      menuItemName: it.menuItemName || it.name || mi.name || 'Item',
      name: it.menuItemName || it.name || mi.name || 'Item',
      quantity: it.quantity || 1,
      price,
      totalPrice: it.totalPrice ?? price * (it.quantity || 1),
      specialInstructions: it.specialInstructions || '',
      imageURL: it.imageURL || it.imageUrl || it.image || mi.menuItemImageURL || mi.imageURL || '',
    };
  });
  return {
    id,
    orderId: d.orderId || id,
    orderNumber: d.orderNumber || d.orderId || id,
    customerId: d.customerId || '',
    customerName: d.customerName || '',
    customerPhone: d.customerPhone || '',
    restaurantId: d.restaurantId || '',
    restaurantName: d.restaurantName || '',
    orderItems: items,
    items,
    orderType: d.orderType || 'Delivery',
    status: parseOrderStatus(d.status),
    rawStatus: d.status,
    subtotal: d.subtotal || 0,
    chefServiceFee: d.chefServiceFee ?? d.serviceFee ?? 0,
    deliveryFee: d.deliveryFee || 0,
    totalAmount: d.totalAmount || d.total || 0,
    total: d.totalAmount || d.total || 0,
    paymentMethod: d.paymentMethod || '',
    paymentStatus: d.paymentStatus || '',
    paymentProvider: d.paymentProvider || '',
    providerTransactionId: d.providerTransactionId || d.stripePaymentIntentId || '',
    jadAuthorizationId: d.jadAuthorizationId || '',
    restaurantLocation: d.restaurantLocation || null,
    customerLocation: d.customerLocation || null,
    deliveryAddress: d.deliveryAddress || null,
    customerAddress: d.customerAddress || d.deliveryAddress?.street || '',
    driverId: d.driverId || null,
    driverName: d.driverName || '',
    driverPayout: d.driverPayout,
    driverCommission: d.driverCommission,
    platformCommission: d.platformCommission,
    restaurantPayout: d.restaurantPayout,
    tipAmount: d.tipAmount || 0,
    specialInstructions: d.specialInstructions || d.deliveryInstructions || '',
    estimatedDeliveryTime: toDate(d.estimatedDeliveryTime),
    pickupTime: toDate(d.pickupTime),
    qrCodeData: d.qrCodeData || '',
    qrCodeActive: d.qrCodeActive,
    qrScannedAt: toDate(d.qrScannedAt),
    restaurantCompleted: d.restaurantCompleted,
    cancellationReason: d.cancellationReason || '',
    cancelledBy: d.cancelledBy || '',
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
    raw: d,
  };
}

// Accepted-order subcollection doc -> plain object (DriverAcceptedOrder model).
// iOS nests order details under `orderDetails`; web writes both flat + nested.
// Read flat first, fall back to orderDetails so both producers work.
export function parseAcceptedOrder(id, d) {
  const od = d.orderDetails || {};
  return {
    id,
    orderId: d.orderId || id,
    driverId: d.driverId || '',
    status: parseDriverStatus(d.status),
    acceptedAt: toDate(d.acceptedAt),
    estimatedPickupTime: toDate(d.estimatedPickupTime),
    actualPickupTime: toDate(d.actualPickupTime),
    estimatedDeliveryTime: toDate(d.estimatedDeliveryTime),
    actualDeliveryTime: toDate(d.actualDeliveryTime),
    pickupLocation: toLatLng(d.pickupLocation) || toLatLng(d.restaurantLocation?.coordinates),
    deliveryLocation: toLatLng(d.deliveryLocation) || toLatLng(d.deliveryAddress?.coordinates),
    totalEarnings: d.totalEarnings ?? d.driverPayout ?? 0,
    driverPayout: d.driverPayout ?? 0,
    driverCommission: d.driverCommission ?? 0,
    platformCommission: d.platformCommission ?? 0,
    deliveryFee: d.deliveryFee ?? 0,
    tipAmount: d.tipAmount ?? 0,
    customerId: d.customerId || od.customerId || '',
    customerName: d.customerName || od.customerName || '',
    customerPhone: d.customerPhone || od.customerPhone || '',
    customerAddress: d.customerAddress || od.customerAddress || d.deliveryAddress?.street || '',
    deliveryAddress: d.deliveryAddress || null,
    restaurantId: d.restaurantId || od.restaurantId || '',
    restaurantName: d.restaurantName || od.restaurantName || '',
    items: d.orderItems || od.items || d.items || [],
    subtotal: d.subtotal ?? od.subtotal ?? 0,
    chefServiceFee: d.chefServiceFee ?? od.chefServiceFee ?? 0,
    totalAmount: d.totalAmount ?? od.totalAmount ?? 0,
    distance: d.distance ?? 0,
    specialInstructions: d.specialInstructions || od.specialInstructions || '',
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
    raw: d,
  };
}

// ---- Delivery zones ----
// Zone = pickup parish (order.restaurantLocation.parish), NOT the customer's
// drop-off parish — iOS DriverDashboardViewModel zone filter parity.
export const PARISHES = [
  'Saint George Basseterre',
  'Saint Peter Basseterre',
  'Saint Anne Sandy Point',
  'Saint John Capisterre',
  'Christ Church Nichola Town',
  'Saint Paul Capisterre',
  'Saint Thomas Middle Island',
  'Saint Thomas Lowland',
  'Saint John Figtree',
  'Saint Paul Charlestown',
  'Saint George Gingerland',
  'Saint James Windward',
  "Saint Mary's Cayon",
  'Saint Thomas Trinity',
];

// Nevis has 5 parishes; the other 9 belong to St. Kitts.
export const NEVIS_PARISHES = new Set([
  'Saint George Gingerland',
  'Saint James Windward',
  'Saint John Figtree',
  'Saint Paul Charlestown',
  'Saint Thomas Lowland',
]);

// Parishes belonging to one island ('saintKitts'/'nevis'/'St. Kitts'/'Nevis'
// all accepted). Unknown island -> full list.
export function parishesForIsland(island) {
  const i = normalizeIsland(island);
  if (!i) return PARISHES;
  return PARISHES.filter((p) => (NEVIS_PARISHES.has(p) ? 'Nevis' : 'St. Kitts') === i);
}

export function normalizeParish(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const v = raw.trim().toLowerCase();
  const hit = PARISHES.find((p) => p.toLowerCase() === v);
  if (hit) return hit;
  if (v === 'basseterre') return 'Saint George Basseterre';
  if (v === 'sandy point') return 'Saint Anne Sandy Point';
  if (v === 'figtree') return 'Saint John Figtree';
  if (v === 'cayon') return "Saint Mary's Cayon";
  return null; // unrecognized -> treat as zone-less (visible to all)
}

// Pickup zone = restaurantLocation.parish (drop-off parish is irrelevant).
export function orderPickupParish(order) {
  const loc = order.restaurantLocation || order.raw?.restaurantLocation;
  return normalizeParish(loc?.parish);
}

// zone null/'' = All Zones; unknown order parish -> visible everywhere.
export function orderMatchesDriverZone(order, zone) {
  const z = normalizeParish(zone);
  if (!z) return true;
  const p = orderPickupParish(order);
  return p == null || p === z;
}
